import type { ContentAction } from '@lumapps/contents/types';

export enum DESIGNER_TRANSLATIONS {
    /**
     * @value All languages
     */
    ALL_LANGUAGES = 'DESIGNER_TRANSLATIONS.ALL_LANGUAGES',
    /**
     * @value All languages (%NB%)
     * @description Text displayed when "All languages" are selected within a list of languages. It also display the number of languages in parenthesis.
     */
    ALL_LANGUAGES_NUMBER = 'DESIGNER_TRANSLATIONS.ALL_LANGUAGES_NUMBER',
    /**
     * @value There are no values that can be autotranslated. Try from another source language.
     * @description Text displayed as an error, when user try to autotranslate with all values empty
     */
    AUTOTRANSLATE_ALL_EMPTY_ERROR = 'DESIGNER_TRANSLATIONS.AUTOTRANSLATE_ALL_EMPTY_ERROR',
    /**
     * @value Auto-translate to all selected languages
     * @description Text displayed as a checkbox label to auto-translate all the fields in all the language the user selected
     */
    AUTOTRANSLATE_ALL_SELECTED_LANGUAGES = 'DESIGNER_TRANSLATIONS.AUTOTRANSLATE_ALL_SELECTED_LANGUAGES',
    /**
     * @value All Autotranslate fields are unchecked. Check the fields where the autotranslation must apply.
     * @description Text displayed as an error, when user try to autotranslate fields without having selected ones
     */
    AUTOTRANSLATE_ALL_UNCHECKED_ERROR = 'DESIGNER_TRANSLATIONS.AUTOTRANSLATE_ALL_UNCHECKED_ERROR',
    /**
     * @value Auto-translate selected fields
     * @description Label on a button allowing user to autotranslate the fields he has selectde
     */
    AUTOTRANSLATE_BUTTON_LABEL = 'DESIGNER_TRANSLATIONS.AUTOTRANSLATE_BUTTON_LABEL',
    /**
     * @value This content is empty. Enter some value to start managing translation.
     * @description Description displayed in a dialog when user try to translate an empty content
     */
    EMPTY_CONTENT_DIALOG_DESCRIPTION = 'DESIGNER_TRANSLATIONS.EMPTY_CONTENT_DIALOG_DESCRIPTION',
    /**
     * @value There is nothing to translate
     * @description Title displayed in a dialog when user try to translate an empty content
     */
    EMPTY_CONTENT_DIALOG_TITLE = 'DESIGNER_TRANSLATIONS.EMPTY_CONTENT_DIALOG_TITLE',
    /**
     * @value This widget is currently empty in the %LANG% version. It cannot be translated.
     * @description Text display on empty body widget, in the content translation context
     */
    EMPTY_WIDGET_MESSAGE = 'DESIGNER_TRANSLATIONS.EMPTY_WIDGET_MESSAGE',
    /**
     * @value Some language versions of this content are not up to date. Auto-translate before publishing.
     */
    MISSING_TRANSLATION_DIALOG_EXPLANATION_MESSAGE = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_MESSAGE',
    /**
     * @value Publish without updates
     */
    MISSING_TRANSLATION_DIALOG_PUBLISH_ANYWAY = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_PUBLISH_ANYWAY',
    /**
     * @value Auto-translate before publishing
     */
    MISSING_TRANSLATION_DIALOG_PUBLISH_TITLE = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_PUBLISH_TITLE',
    /**
     * @value Save without updates
     */
    MISSING_TRANSLATION_DIALOG_SAVE_ANYWAY = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SAVE_ANYWAY',
    /**
     * @value Auto-translate before saving
     */
    MISSING_TRANSLATION_DIALOG_SAVE_TITLE = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SAVE_TITLE',
    /**
     * @value Schedule without updates
     */
    MISSING_TRANSLATION_DIALOG_SCHEDULE_ANYWAY = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SCHEDULE_ANYWAY',
    /**
     * @value Auto-translate before scheduling
     */
    MISSING_TRANSLATION_DIALOG_SCHEDULE_TITLE = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SCHEDULE_TITLE',
    /**
     * @value Submit without updates
     */
    MISSING_TRANSLATION_DIALOG_SUBMIT_ANYWAY = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SUBMIT_ANYWAY',
    /**
     * @value Auto-translate before submitting
     */
    MISSING_TRANSLATION_DIALOG_SUBMIT_TITLE = 'DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SUBMIT_TITLE',
    /**
     * @value Not updated
     */
    NOT_UPDATED = 'DESIGNER_TRANSLATIONS.NOT_UPDATED',
    /**
     * @value Choose source language
     */
    SOURCE_LANGUAGE_LABEL = 'DESIGNER_TRANSLATIONS.SOURCE_LANGUAGE_LABEL',
    /**
     * @value Switch and discard
     * @description Button label display on the alert dialog when user want to switch language
     */
    SWITCH_ALERT_CONFIRM = 'DESIGNER_TRANSLATIONS.SWITCH_ALERT_CONFIRM',
    /**
     * @value Switching to another language discards your previous edits.
     * @description Message displayed on the alert dialog when user want to switch language , explaining what will happen after the switch
     */
    SWITCH_ALERT_MESSAGE_EXPLANATION = 'DESIGNER_TRANSLATIONS.SWITCH_ALERT_MESSAGE_EXPLANATION',
    /**
     * @value To save your previous edits, click Save and insert.
     * @description Message displayed on the alert dialog when user want to switch language, indicating to the user how to not lose his changes
     */
    SWITCH_ALERT_MESSAGE_HELP = 'DESIGNER_TRANSLATIONS.SWITCH_ALERT_MESSAGE_HELP',
    /**
     * @value Switch language?
     * @description Title display on the alert dialog when user want to switch language
     */
    SWITCH_ALERT_TITLE = 'DESIGNER_TRANSLATIONS.SWITCH_ALERT_TITLE',
    /**
     * @value Choose target language
     */
    TARGET_LANGUAGE_LABEL = 'DESIGNER_TRANSLATIONS.TARGET_LANGUAGE_LABEL',
    /**
     * @value Translate changes
     */
    TRANSLATE_CHANGES = 'DESIGNER_TRANSLATIONS.TRANSLATE_CHANGES',
    /**
     * @value Manage content translation
     * @description Title in a dialog in the context of widgets translation
     */
    TRANSLATION_DIALOG_TITLE = 'DESIGNER_TRANSLATIONS.TRANSLATION_DIALOG_TITLE',
    /**
     * @value View in
     * @description Label display to allow the user to view his content in a specific language (the label is tied to a select)
     */
    VIEW_IN = 'DESIGNER_TRANSLATIONS.VIEW_IN',
}
export const MISSING_TRANSLATION_DIALOG_TITLE_KEYS: Record<ContentAction, DESIGNER_TRANSLATIONS> = {
    publish: DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_PUBLISH_TITLE,
    save: DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SAVE_TITLE,
    schedule: DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SCHEDULE_TITLE,
    'submit-for-review': DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SUBMIT_TITLE,
};

export const MISSING_TRANSLATION_DIALOG_SKIP_KEYS: Record<ContentAction, DESIGNER_TRANSLATIONS> = {
    publish: DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_PUBLISH_ANYWAY,
    save: DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SAVE_ANYWAY,
    schedule: DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SCHEDULE_ANYWAY,
    'submit-for-review': DESIGNER_TRANSLATIONS.MISSING_TRANSLATION_DIALOG_SUBMIT_ANYWAY,
};
